import {
  POS_CART_ADD,
  POS_CART_INC,
  POS_CART_DEC,
  POS_CART_SET_QTY,
  POS_CART_SET_PRICE,
  POS_CART_REMOVE,
  POS_CART_CLEAR,
  POS_CART_REPLACE,
  POS_CART_SET_DINEIN,
  POS_CART_CLEAR_DINEIN,
  POS_CART_START_REFUND,
  POS_CART_END_REFUND,
} from "./actionTypes";

// Cart lines are keyed by _key so the same item+addons combination stacks.
// Phase 2 has no addon-selection UI yet, so _key === item id.
const initialState = {
  lines: [],
  dineIn: null, // { table_id, table_name, party_size } when the sale is at a table
  // When set, the cart is giving money back for a paid bill instead of selling.
  // { order_id, unique_order_id, receipt_code, order_total, refundable_total,
  //   refunded_total, card_available, cash_available, tax_percent }
  refund: null,
};

// Open/general lines pass their own `_key` so two of them with different names
// stay separate lines instead of stacking as one product.
function keyFor(item) {
  return item._key ? String(item._key) : String(item.id);
}

// In refund mode a line can never exceed what is still refundable on the
// original sale — that ceiling travels on the line as maxQuantity/maxPrice.
function capQty(line, quantity) {
  const max = line.maxQuantity;
  const q = Math.max(0, quantity);
  return max != null ? Math.min(q, max) : q;
}

function capPrice(line, price) {
  const p = Math.max(0, Number(price) || 0);
  return line.maxPrice != null ? Math.min(p, line.maxPrice) : p;
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case POS_CART_ADD: {
      // A refund cart is a mirror of one specific bill — selling into it would
      // silently mix a return and a sale, so new items are refused outright.
      if (state.refund) return state;

      const item = action.payload;
      const key = keyFor(item);
      const existing = state.lines.find((l) => l._key === key);
      if (existing) {
        return {
          ...state,
          lines: state.lines.map((l) =>
            l._key === key ? { ...l, quantity: l.quantity + 1 } : l
          ),
        };
      }
      return {
        ...state,
        lines: [
          ...state.lines,
          {
            _key: key,
            item_id: item.id,
            name: item.name,
            price: Number(item.price) || 0,
            image: item.image || null,
            quantity: 1,
            addons: [],
            // Open/general item: the cashier typed this name, so it has to
            // travel to the server with the line (see cartToItems).
            ...(item.custom ? { custom: true } : {}),
          },
        ],
      };
    }

    case POS_CART_INC:
      return {
        ...state,
        lines: state.lines.map((l) =>
          l._key === action.payload ? { ...l, quantity: capQty(l, l.quantity + 1) } : l
        ),
      };

    // The − stepper floors at 1 (minimum interval). Removing a line is a
    // deliberate action via the line's remove (×) button, not by stepping down.
    case POS_CART_DEC:
      return {
        ...state,
        lines: state.lines.map((l) =>
          l._key === action.payload
            ? { ...l, quantity: Math.max(1, l.quantity - 1) }
            : l
        ),
      };

    case POS_CART_SET_QTY:
      return {
        ...state,
        lines: state.lines
          .map((l) =>
            l._key === action.payload.key
              ? { ...l, quantity: capQty(l, action.payload.quantity) }
              : l
          )
          .filter((l) => l.quantity > 0),
      };

    // Manual unit-price override (open price / on-the-spot discount). Flagged
    // with priceEdited so the UI can mark the line. In refund mode the price is
    // additionally capped at what the customer actually paid for that unit.
    case POS_CART_SET_PRICE:
      return {
        ...state,
        lines: state.lines.map((l) =>
          l._key === action.payload.key
            ? {
                ...l,
                price: capPrice(l, action.payload.price),
                priceEdited: true,
              }
            : l
        ),
      };

    case POS_CART_REMOVE:
      return {
        ...state,
        lines: state.lines.filter((l) => l._key !== action.payload),
      };

    case POS_CART_SET_DINEIN:
      return { ...state, dineIn: action.payload };

    case POS_CART_CLEAR_DINEIN:
      return { ...state, dineIn: null };

    case POS_CART_CLEAR:
      return initialState;

    case POS_CART_REPLACE:
      return {
        lines: Array.isArray(action.payload.lines) ? action.payload.lines : [],
        dineIn: action.payload.dineIn || null,
        refund: null,
      };

    case POS_CART_START_REFUND:
      return {
        lines: Array.isArray(action.payload.lines) ? action.payload.lines : [],
        dineIn: null,
        refund: action.payload.refund || null,
      };

    case POS_CART_END_REFUND:
      return initialState;

    default:
      return state;
  }
}
